import { Link, NavLink, type LinkProps, type NavLinkProps } from "react-router";
import { useActiveLocale } from "./LocaleContext";
import { localePath } from "./localePath";

/**
 * LocaleLink / LocaleNavLink — locale-aware wrappers over React Router's `<Link>`
 * / `<NavLink>` (Story 4.1, AC4). They read the active locale from context and
 * prefix a string `to` via `localePath`, so every nav/CTA/footer link from
 * `/nl/…` stays under `/nl/…` (and `/en/…` under `/en/…`) — no link drops the
 * prefix and 301s/404s.
 *
 * Outside a `:lang` subtree (the sandbox routes) there is no active locale, so
 * `localePath` is a no-op and these render exactly like the bare RR components —
 * which is why shared components (SiteNav at /nav-lab, ds Button, …) can adopt
 * them with zero behavior change on the sandbox.
 *
 * Object `to` values (rare) pass through untouched — 4.1 only localizes string
 * paths, which is the entire current link surface.
 */
export function LocaleLink({ to, ...rest }: LinkProps) {
  const code = useActiveLocale()?.code;
  return <Link to={typeof to === "string" ? localePath(code, to) : to} {...rest} />;
}

export function LocaleNavLink({ to, ...rest }: NavLinkProps) {
  const code = useActiveLocale()?.code;
  return <NavLink to={typeof to === "string" ? localePath(code, to) : to} {...rest} />;
}
